<template>
  <div
    :class="DS_ICON_PICKER_LIST_CLASSES.Categories"
    role="group"
    :aria-label="CATEGORIES_LABEL"
    data-cy="ds-icon-picker-list__categories"
  >
    <DSButton
      v-for="category in categories"
      :key="category.id"
      :class="DS_ICON_PICKER_LIST_CLASSES.Category"
      :variant="category.id === modelValue ? DS_BUTTON_VARIANT.Primary : DS_BUTTON_VARIANT.Ghost"
      :size="DS_SIZE.Medium"
      :disabled="props.disabled"
      :prefix-icon="category.icon"
      :aria-label="category.icon ? category.label : undefined"
      :aria-pressed="category.id === modelValue"
      :data-cy="`ds-icon-picker-list__category--${category.id}`"
      @click="modelValue = category.id"
      @mouseenter="changeHoveredCategory(category.id)"
      @mouseleave="changeHoveredCategory(null)"
    >
      <template v-if="!category.icon">
        {{ category.label }}
      </template>
    </DSButton>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import { DS_BUTTON_VARIANT } from "@/src/design_system/button_and_controls/button/types/props";
import { DS_SIZE } from "@/src/design_system/shared/types/size";
import { DS_ICON_PICKER_LIST_CLASSES } from "@/src/design_system/form_elements/icon_picker/constants/classes";
import {
  ICON_PICKER_CATEGORIES,
  ICON_PICKER_CATEGORY_ID,
  type IconPickerCategoryId,
} from "@/src/design_system/form_elements/icon_picker/constants/categories";
import type { DSIconPickerCategoriesEmits } from "@/src/design_system/form_elements/icon_picker/types/emits";
import type { DSIconPickerCategoriesProps } from "@/src/design_system/form_elements/icon_picker/types/props";
import { getIconPickerCategoryLabel } from "@/src/design_system/form_elements/icon_picker/utils/categoryLabels";

const props = withDefaults(defineProps<DSIconPickerCategoriesProps>(), {
  disabled: false,
});

const emit = defineEmits<DSIconPickerCategoriesEmits>();

const modelValue = defineModel<IconPickerCategoryId>({ default: ICON_PICKER_CATEGORY_ID.All });

const CATEGORIES_LABEL = window.I18n.designSystem.iconPicker.categoriesLabel;

const categories = computed(() => {
  return ICON_PICKER_CATEGORIES.map(category => ({
    ...category,
    label: getIconPickerCategoryLabel(category),
  }));
});

const changeHoveredCategory = (categoryId: IconPickerCategoryId | null): void => {
  if (props.disabled && categoryId !== null) return;

  emit("hovered-category:change", categoryId);
};
</script>
